import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ShieldCheck, Eye, Sparkles, Save, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import authedFetch from '../../../lib/authedFetch';
import secureStorage from '../../../lib/secureStorage';
import { invalidateModulesCache } from '../../../lib/useModules';
import { useAuth } from '../../../context/AuthContext';
import logger from '../../../lib/logger';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny relative-time helper for the "saved 2h ago" badges in the
// recently-saved strip. Inlined to keep ModulesTab self-contained.
function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * ModulesTab — super-admin only matrix of "what modules are
 * enabled for which org / which role".
 *
 * Two views:
 *   • Per-org: rows = orgs, columns = modules, switches per cell.
 *     Group rows render bold; child page modules indent.
 *   • Per-role: rows = roles (org_admin, user), same columns.
 *
 * Resolution rule (backend): module enabled ⇔ org_setting AND
 * role_setting (with parent-group cascade). Locked modules
 * (admin group + Org & User Mgmt) render disabled switches
 * with a tooltip explaining why.
 */
export default function ModulesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const { setViewAsOrg } = useAuth();

  const previewAsUser = useCallback((org) => {
    // Park a module-preview role + org in sessionStorage. authedFetch
    // surfaces these as X-View-As-Org / X-View-As-Role on every call,
    // which the backend honours only for super admins (so the
    // session token is the gating factor — non-super-admins can't
    // spoof their way into other orgs by setting these headers).
    sessionStorage.setItem('viewAsRole', 'user');
    setViewAsOrg(org.id, org.name);
    invalidateModulesCache();
    toast.success(`Previewing as user in ${org.name}. The org switcher in the header exits preview.`);
    // Soft refresh: the sidebar re-renders via the modules cache
    // invalidation; full reload would lose the admin's place.
  }, [setViewAsOrg]);

  // ── Preset bulk-apply ─────────────────────────────────────────
  // Single dropdown + target-org selector above the matrix.
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedOrgForPreset, setSelectedOrgForPreset] = useState('');
  const [applying, setApplying] = useState(false);
  // Save-as-preset dialog state
  const [saveDialogOrg, setSaveDialogOrg] = useState(null);
  const [presetLabel, setPresetLabel] = useState('');
  const [presetDesc, setPresetDesc] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  const openSaveAsPreset = (org) => {
    setSaveDialogOrg(org);
    setPresetLabel('');
    setPresetDesc('');
  };

  const submitSavePreset = async () => {
    if (!saveDialogOrg) return;
    if (!presetLabel.trim()) {
      toast.error('Give the preset a name');
      return;
    }
    setSavingPreset(true);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/admin/modules/presets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: presetLabel.trim(),
          description: presetDesc.trim(),
          source_org_id: saveDialogOrg.id,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const body = await r.json();
      setPresets((prev) => [...prev, body.preset]);
      setSaveDialogOrg(null);
      toast.success(`Saved "${body.preset.label}" — appears in the preset dropdown above`);
    } catch (e) {
      toast.error(e.message || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (preset) => {
    if (!preset?.id?.startsWith('custom_')) return;
    if (!window.confirm(`Delete preset "${preset.label}"? This can't be undone.`)) return;
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/admin/modules/presets/${preset.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      if (selectedPreset === preset.id) setSelectedPreset('');
      toast.success(`Deleted "${preset.label}"`);
    } catch (e) {
      toast.error(e.message || 'Failed to delete preset');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const r = await authedFetch(`${API_URL}/api/admin/modules/presets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const body = await r.json();
          setPresets(body.presets || []);
        }
      } catch (e) { logger.warn('preset fetch failed', e); }
    })();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/admin/modules/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setData(payload);
    } catch (e) {
      logger.error('ModulesTab fetch failed', e);
      toast.error('Could not load module settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const applyPreset = async () => {
    if (!selectedPreset || !selectedOrgForPreset) {
      toast.error('Pick a preset and a target org first');
      return;
    }
    setApplying(true);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/admin/modules/apply-preset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: selectedOrgForPreset, preset_id: selectedPreset }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const body = await r.json();
      const previousSettings = body.previous_settings || {};
      toast.success(
        `Applied "${body.preset_label}" to ${body.org_name} (${body.applied_count} modules)`,
        {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                const r2 = await authedFetch(`${API_URL}/api/admin/modules/restore-settings`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ org_id: body.org_id, settings: previousSettings }),
                });
                if (r2.ok) {
                  toast.success(`Reverted ${body.org_name} back to its prior modules`);
                  await refresh();
                  invalidateModulesCache();
                } else {
                  toast.error('Undo failed — please re-apply manually');
                }
              } catch (e) {
                toast.error(e.message || 'Undo failed');
              }
            },
          },
          duration: 10_000,
        }
      );
      await refresh();
      invalidateModulesCache();
    } catch (e) {
      toast.error(e.message || 'Failed to apply preset');
    } finally {
      setApplying(false);
    }
  };

  const setSetting = async (scope, scopeId, moduleId, enabled) => {
    const key = `${scope}:${scopeId}:${moduleId}`;
    setSavingKey(key);
    try {
      const token = secureStorage.getItem('session_token');
      const url = scope === 'org'
        ? `${API_URL}/api/admin/modules/org/${scopeId}`
        : `${API_URL}/api/admin/modules/role/${scopeId}`;
      const r = await authedFetch(url, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_id: moduleId, enabled }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      // Mutate locally — avoids a full refetch + flicker
      setData((prev) => {
        if (!prev) return prev;
        const next = { ...prev, settings: { ...prev.settings } };
        if (scope === 'org') {
          next.settings.by_org = { ...next.settings.by_org };
          next.settings.by_org[scopeId] = { ...(next.settings.by_org[scopeId] || {}), [moduleId]: enabled };
        } else {
          next.settings.by_role = { ...next.settings.by_role };
          next.settings.by_role[scopeId] = { ...(next.settings.by_role[scopeId] || {}), [moduleId]: enabled };
        }
        return next;
      });
      // Invalidate /me/modules cache so the sidebar / route guards
      // re-fetch on next mount (super admin's own view doesn't
      // change since they see everything, but next page load by
      // an affected user will pick up the new state).
      invalidateModulesCache();
    } catch (e) {
      toast.error(e.message || 'Failed to update module setting');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading || !data) {
    return (
      <Card data-testid="modules-tab-loading">
        <CardContent className="py-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-sm text-slate-500">Loading module catalog…</p>
        </CardContent>
      </Card>
    );
  }

  const { orgs, roles, modules, settings } = data;

  const renderSwitch = (scope, scopeId, mod) => {
    const settingMap = scope === 'org' ? settings.by_org[scopeId] : settings.by_role[scopeId];
    const explicit = settingMap?.[mod.id];
    const value = explicit !== undefined ? explicit : (mod.default_enabled !== false);
    const key = `${scope}:${scopeId}:${mod.id}`;
    const locked = !!mod.locked;
    return (
      <div className="flex items-center gap-1.5">
        <Switch
          checked={value}
          disabled={locked || savingKey === key}
          onCheckedChange={(next) => setSetting(scope, scopeId, mod.id, next)}
          data-testid={`module-switch-${scope}-${scopeId}-${mod.id}`}
        />
        {explicit === undefined && (
          <span className="text-[9px] uppercase tracking-wide text-slate-400" title="Using default — toggle to override">
            default
          </span>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="modules-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-teal-600" />
          Functional Modules
        </CardTitle>
        <CardDescription>
          Toggle which modules each <strong>organization</strong> and <strong>role</strong> can access.
          A module is visible to a user only when its <strong>org</strong> AND <strong>role</strong> settings both allow it
          (parent-group cascade applies). Locked modules can&apos;t be disabled — they&apos;re required for admins to manage the platform.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Recently saved custom presets — institutional memory
            for the next sales engineer onboarding a tenant. */}
        {presets.filter((p) => p.kind === 'custom').length > 0 && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="modules-recent-presets-strip">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-900">
                Recently saved by your team
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {presets.filter((p) => p.kind === 'custom').slice(0, 3).map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setSelectedPreset(p.id)}
                  className={`group flex items-start gap-2 rounded-md border bg-white px-3 py-2 text-left text-xs transition hover:border-emerald-400 hover:shadow-sm ${
                    selectedPreset === p.id ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-slate-200'
                  }`}
                  data-testid={`recent-preset-${p.id}`}
                >
                  <Sparkles className="h-3 w-3 mt-0.5 text-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate max-w-[220px]">{p.label}</div>
                    <div className="text-[11px] text-slate-500">
                      {p.source_org_name && <span>from <span className="font-medium">{p.source_org_name}</span> · </span>}
                      saved {relativeTime(p.created_at)}
                      {p.created_by_email && <span> by <span className="font-mono">{p.created_by_email}</span></span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {presets.length > 0 && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/30 p-4" data-testid="modules-preset-bar">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-900">Bulk apply preset</span>
              <span className="text-xs text-slate-500">— one click for sales engineers spinning up a new tenant</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedPreset}
                onChange={(e) => setSelectedPreset(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-amber-400 focus:outline-none"
                data-testid="modules-preset-select"
              >
                <option value="">Pick a preset…</option>
                {presets.filter((p) => p.kind !== 'custom').length > 0 && (
                  <optgroup label="Built-in">
                    {presets.filter((p) => p.kind !== 'custom').map((p) => (
                      <option key={p.id} value={p.id} title={p.description}>
                        {p.label}
                      </option>
                    ))}
                  </optgroup>
                )}
                {presets.filter((p) => p.kind === 'custom').length > 0 && (
                  <optgroup label="Custom (saved by your team)">
                    {presets.filter((p) => p.kind === 'custom').map((p) => (
                      <option key={p.id} value={p.id} title={p.description}>
                        {p.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <span className="text-xs text-slate-400">applies to</span>
              <select
                value={selectedOrgForPreset}
                onChange={(e) => setSelectedOrgForPreset(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-amber-400 focus:outline-none"
                data-testid="modules-preset-org-select"
              >
                <option value="">Pick an org…</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <Button
                onClick={applyPreset}
                disabled={!selectedPreset || !selectedOrgForPreset || applying}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="modules-preset-apply-btn"
              >
                {applying ? 'Applying…' : 'Apply preset'}
              </Button>
              {selectedPreset && (
                <span className="ml-2 text-xs text-slate-500 italic max-w-md truncate" data-testid="modules-preset-description">
                  {presets.find((p) => p.id === selectedPreset)?.description}
                </span>
              )}
              {selectedPreset && presets.find((p) => p.id === selectedPreset)?.kind === 'custom' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deletePreset(presets.find((p) => p.id === selectedPreset))}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto"
                  data-testid="modules-preset-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete preset
                </Button>
              )}
            </div>
          </div>
        )}
        <Tabs defaultValue="by-org" className="space-y-4">
          <TabsList>
            <TabsTrigger value="by-org" data-testid="modules-tab-by-org">By Organization</TabsTrigger>
            <TabsTrigger value="by-role" data-testid="modules-tab-by-role">By Role</TabsTrigger>
          </TabsList>

          <TabsContent value="by-org">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 sticky left-0 bg-white">Module</th>
                    {orgs.map((o) => (
                      <th key={o.id} className="text-left py-2 px-3 whitespace-nowrap" title={o.id}>
                        <div className="flex items-center gap-1">
                          <span>{o.name}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-400 hover:text-amber-600"
                            onClick={() => previewAsUser(o)}
                            title={`Preview as a regular user in ${o.name}`}
                            data-testid={`module-preview-as-user-${o.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-400 hover:text-emerald-600"
                            onClick={() => openSaveAsPreset(o)}
                            title={`Save ${o.name}'s current modules as a preset`}
                            data-testid={`module-save-as-preset-${o.id}`}
                          >
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.id} className={m.type === 'group' ? 'bg-slate-50/60' : ''}>
                      <td className={`py-1.5 pr-4 sticky left-0 ${m.type === 'group' ? 'bg-slate-50/60 font-semibold' : 'bg-white pl-6'}`}>
                        {m.label}
                        {m.locked && <Badge variant="outline" className="ml-2 text-[9px] py-0">locked</Badge>}
                      </td>
                      {orgs.map((o) => (
                        <td key={o.id} className="py-1.5 px-3">
                          {renderSwitch('org', o.id, m)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="by-role">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 sticky left-0 bg-white">Module</th>
                    {roles.map((r) => (
                      <th key={r} className="text-left py-2 px-3 whitespace-nowrap capitalize">
                        {r.replace('_', ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.id} className={m.type === 'group' ? 'bg-slate-50/60' : ''}>
                      <td className={`py-1.5 pr-4 sticky left-0 ${m.type === 'group' ? 'bg-slate-50/60 font-semibold' : 'bg-white pl-6'}`}>
                        {m.label}
                        {m.locked && <Badge variant="outline" className="ml-2 text-[9px] py-0">locked</Badge>}
                      </td>
                      {roles.map((r) => (
                        <td key={r} className="py-1.5 px-3">
                          {renderSwitch('role', r, m)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Save-as-preset dialog. Opens when an admin clicks the
          green Save icon in any org column header. */}
      <Dialog open={!!saveDialogOrg} onOpenChange={(v) => { if (!v) setSaveDialogOrg(null); }}>
        <DialogContent data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save {saveDialogOrg?.name}&apos;s modules as a preset</DialogTitle>
            <DialogDescription>
              Captures the modules currently enabled for <strong>{saveDialogOrg?.name}</strong> so a future
              tenant can be configured the same way in one click. The preset will appear in the
              dropdown above for everyone with super-admin access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Preset name</label>
              <Input
                value={presetLabel}
                onChange={(e) => setPresetLabel(e.target.value)}
                placeholder="e.g. Mid-market trial"
                maxLength={80}
                data-testid="save-preset-label"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description (optional)</label>
              <Input
                value={presetDesc}
                onChange={(e) => setPresetDesc(e.target.value)}
                placeholder="Why does this preset exist? Who's it for?"
                maxLength={280}
                data-testid="save-preset-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveDialogOrg(null)} disabled={savingPreset}>Cancel</Button>
            <Button
              onClick={submitSavePreset}
              disabled={savingPreset || !presetLabel.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-preset-submit"
            >
              {savingPreset ? 'Saving…' : 'Save preset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
